import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Star Rating Component
 * Müşteri değerlendirmesi için yıldız puanlama bileşeni
 *
 * @param {number} rating - Mevcut puan (0-5)
 * @param {function} onRate - Puan değiştiğinde çağrılan callback
 * @param {boolean} readonly - Salt okunur mod
 * @param {string} size - Boyut ('sm', 'md', 'lg')
 * @param {number} maxStars - Maksimum yıldız sayısı
 */
export default function StarRating({
  rating = 0,
  onRate,
  readonly = false,
  size = 'md',
  maxStars = 5,
  className,
}) {
  const [hoverRating, setHoverRating] = useState(0);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const gapClasses = {
    sm: 'gap-0.5',
    md: 'gap-1',
    lg: 'gap-1.5',
  };

  const handleClick = (starIndex) => {
    if (!readonly && onRate) {
      // Aynı yıldıza tıklanırsa sıfırla
      if (starIndex === rating) {
        onRate(0);
      } else {
        onRate(starIndex);
      }
    }
  };

  const handleMouseEnter = (starIndex) => {
    if (!readonly) {
      setHoverRating(starIndex);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverRating(0);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      className={cn(
        'flex items-center',
        gapClasses[size],
        !readonly && 'cursor-pointer',
        className
      )}
      onMouseLeave={handleMouseLeave}
    >
      {Array.from({ length: maxStars }, (_, index) => {
        const starIndex = index + 1;
        const isFilled = starIndex <= displayRating;
        const isHovered = !readonly && hoverRating >= starIndex;

        return (
          <button
            key={starIndex}
            type="button"
            disabled={readonly}
            onClick={() => handleClick(starIndex)}
            onMouseEnter={() => handleMouseEnter(starIndex)}
            className={cn(
              'transition-all duration-150 focus:outline-none',
              readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110',
              'disabled:cursor-default'
            )}
          >
            <Star
              className={cn(
                sizeClasses[size],
                'transition-colors duration-150',
                isFilled
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-transparent text-slate-300',
                isHovered && !isFilled && 'text-amber-300'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Rating'i metin olarak döndürür
 * @param {number} rating - Puan
 * @returns {string} Metin açıklaması
 */
export function getRatingText(rating) {
  const texts = {
    0: 'Nicht bewertet',
    1: 'Sehr schlecht',
    2: 'Schlecht',
    3: 'Durchschnittlich',
    4: 'Gut',
    5: 'Ausgezeichnet',
  };
  return texts[rating] || texts[0];
}

/**
 * Rating'e göre renk sınıfı döndürür
 * @param {number} rating - Puan
 * @returns {string} Tailwind renk sınıfı
 */
export function getRatingColor(rating) {
  if (rating >= 4) return 'text-emerald-600';
  if (rating >= 3) return 'text-amber-600';
  if (rating >= 1) return 'text-red-600';
  return 'text-slate-400';
}
